import { noop } from 'lodash';
import React, { PureComponent } from 'react';
import styled, { withTheme, css } from 'styled-components';
import { Zee } from '@zocdoc-frontend-common/assets-icons';
import { Link } from '@zocdoc-frontend-common/components-link';
import {
    FacebookLink,
    LinkedInLink,
    TwitterLink,
    InstagramLink,
} from '@zocdoc-frontend-common/components-link-farm';
import PaddedContainer from '@zocdoc-frontend-common/components-padded-container';
import { breakpoint } from '@zocdoc-frontend-common/styles-grid';
import { filterToValidAttributes } from '@zocdoc-frontend-common/utils-dom-property';
import {
    Metrics,
    MetricsClickable,
} from '@zocdoc-frontend-common/utils-frontend-metrics';
import removeWhitespace from '@zocdoc-frontend-common/utils-stringutils/removeWhitespace';
import { white } from '@zocdoc/branding/colors';
import { LinkFarmToggle } from '../LinkFarmToggle';
import Sitemap from '../Sitemap';

export function shouldRenderLink(uri, location) {
    if (!uri) {
        return false;
        // do not render link to homepage when current route is the homepage
    } else if (location && uri === location.pathname) {
        return false;
    }
    return true;
}

/**
 * @param {string} hostOverride
 * @param {{
 *     pathname: string,
 * }} location
 * @param {boolean} showSiteMap
 * @param {Object} theme
 * @param {function(): void} trackLinkClick
 * @returns {JSX.Element}
 */
class Legal extends PureComponent {
    static defaultProps = {
        trackLinkClick: noop,
        hostOverride: '',
    };

    state = {
        viewSitemap: false,
    };

    renderCustomContent(definition, index) {
        if (shouldRenderLink(definition.uri, this.props.location)) {
            const shouldOverride = !definition.uri.startsWith('https');
            const host = shouldOverride ? this.props.hostOverride : '';

            return (
                <MetricsClickable
                    key={index}
                    action={`Click ${definition.text}`}
                    component={
                        definition.componentName
                            ? `${definition.componentName}Links`
                            : 'CustomContent'
                    }
                    element={removeWhitespace(definition.text)}
                >
                    <StyledLink
                        beforeClick={this.handleBeforeCustomLinkClick}
                        data-component-name={definition.componentName}
                        data-test="footer-legal-custom-content-link"
                        href={`${host}${definition.uri}`}
                        target="_blank"
                        title={definition.title}
                    >
                        {definition.text}
                    </StyledLink>
                </MetricsClickable>
            );
        } else {
            return (
                <ColoredText key={index} textColor={definition.textColor}>
                    {definition.text}
                </ColoredText>
            );
        }
    }

    maybeRenderSocialLinks(shouldRenderSocialLinks) {
        const { linkColor } = this.props.theme.footer.legal;
        if (shouldRenderSocialLinks) {
            return (
                <Metrics component="SocialMediaLinks">
                    <SocialLinkGroup data-test="social-media-links-group">
                        <MetricsClickable
                            action="Click Twitter"
                            element="Twitter"
                        >
                            <TwitterLink
                                color={linkColor}
                                height={20}
                                onBeforeLinkClick={
                                    this.handleBeforeSocialLinkClick
                                }
                            />
                        </MetricsClickable>

                        <MetricsClickable
                            action="Click Instagram"
                            element="Instagram"
                        >
                            <InstagramLink
                                color={linkColor}
                                height={20}
                                onBeforeLinkClick={
                                    this.handleBeforeSocialLinkClick
                                }
                            />
                        </MetricsClickable>

                        <MetricsClickable
                            action="Click Facebook"
                            element="Facebook"
                        >
                            <FacebookLink
                                color={linkColor}
                                height={20}
                                onBeforeLinkClick={
                                    this.handleBeforeSocialLinkClick
                                }
                            />
                        </MetricsClickable>

                        <MetricsClickable
                            action="Click LinkedIn"
                            element="LinkedIn"
                        >
                            <LinkedInLink
                                color={linkColor}
                                height={20}
                                onBeforeLinkClick={
                                    this.handleBeforeSocialLinkClick
                                }
                            />
                        </MetricsClickable>
                    </SocialLinkGroup>
                </Metrics>
            );
        }
    }

    maybeRenderSitemapToggle(shouldRenderSitemapToggle) {
        if (shouldRenderSitemapToggle) {
            return (
                <DesktopOnly>
                    <LinkFarmToggle
                        toggleText="Site map"
                        toggleState={this.state.viewSitemap}
                        onToggleClick={this.handleSitemapToggleClick}
                    />
                </DesktopOnly>
            );
        }
    }

    handleSitemapToggleClick = () => {
        this.setState((prevState) => ({ viewSitemap: !prevState.viewSitemap }));
    };

    maybeRenderSitemap(shouldRenderSitemap) {
        if (shouldRenderSitemap) {
            return (
                <DesktopOnly>
                    <Sitemap
                        isVisible={this.state.viewSitemap}
                        hostOverride={this.props.hostOverride}
                    />
                </DesktopOnly>
            );
        }
    }

    renderCopyright(isWhiteLabel) {
        const copyrightContent = {
            text: `© ${new Date().getFullYear()} Zocdoc, Inc.`,
            componentName: 'Zocdoc',
            textColor: isWhiteLabel ? undefined : white,
            uri: isWhiteLabel ? '/' : '',
        };
        return isWhiteLabel ? (
            this.renderCustomContent(copyrightContent)
        ) : (
            <CopyrightNotice>
                <StyledZee color="white" expression="smile" />
                {this.renderCustomContent(copyrightContent)}
            </CopyrightNotice>
        );
    }

    render() {
        const { theme, ...props } = this.props;
        const isWhiteLabel = theme.directoryId !== '-1';

        return (
            <BackgroundColorContainer>
                <StyledContainer {...filterToValidAttributes(props)}>
                    {isWhiteLabel ? (
                        <TextLinkGroup data-test="footer-legal-custom-content-link-group" />
                    ) : null}
                    <TextLinkGroup data-test="footer-legal-custom-content-link-group">
                        {this.renderCopyright(isWhiteLabel)}
                        {this.renderCustomContent({
                            text: 'Terms',
                            uri: '/about/terms/',
                            componentName: 'Zocdoc',
                        })}
                        {this.renderCustomContent({
                            text: 'Privacy',
                            uri: '/about/privacypolicy/',
                            componentName: 'Zocdoc',
                        })}
                        {this.renderCustomContent({
                            text: 'Do not sell my personal information',
                            uri: '/privacy-settings',
                            componentName: 'Zocdoc',
                        })}
                        {this.maybeRenderSitemapToggle(
                            this.props.showSiteMap && !isWhiteLabel
                        )}
                    </TextLinkGroup>

                    {this.maybeRenderSocialLinks(!isWhiteLabel)}
                </StyledContainer>

                {this.maybeRenderSitemap(
                    this.props.showSiteMap && !isWhiteLabel
                )}
            </BackgroundColorContainer>
        );
    }

    handleBeforeSocialLinkClick = async (e) => {
        const link = e.currentTarget;
        const elementName = link.getAttribute('data-element-name');

        await this.props.trackLinkClick({
            action: `Click ${elementName}`,
            Component: 'SocialMediaLinks',
            Element: removeWhitespace(elementName),
        });
    };

    handleBeforeCustomLinkClick = async (e) => {
        const link = e.currentTarget;
        const elementName = link.textContent;
        const componentName = link.getAttribute('data-component-name');

        await this.props.trackLinkClick({
            action: `Click ${elementName}`,
            Component: `${componentName}Links` || 'CustomContent',
            Element: removeWhitespace(elementName),
        });
    };
}

const FooterText = css`
    font-size: 12px;
    line-height: 1.67;

    ${breakpoint.medium} {
        font-size: 14px;
        line-height: 1.4;
    }
`;

const BackgroundColorContainer = styled.div`
    background-color: ${(p) => p.theme.footer.legal.bgColor};
    flex-grow: 1;

    ${breakpoint.medium} {
        background-color: #000024;
    }
`;

const StyledContainer = styled(PaddedContainer)`
    align-content: flex-start;
    color: ${(p) => p.theme.footer.legal.textColor};
    display: flex;
    flex-wrap: wrap;

    padding: 16px;
    ${breakpoint.medium} {
        padding: 15px 53px;
    }
`;

export const ColoredText = styled.span`
    color: ${(p) => p.textColor};
    ${FooterText}
`;

export const StyledLink = styled(Link)`
    border-bottom-color: ${(p) => p.theme.footer.legal.linkUnderlineColor};
    color: ${(p) => p.theme.footer.legal.linkColor};

    &:focus,
    &:hover {
        border-bottom-color: ${(p) =>
            p.theme.footer.legal.linkUnderlineColorHover};
        color: ${(p) => p.theme.footer.legal.linkColorHover};
    }

    &:visited {
        border-bottom-color: ${(p) =>
            p.theme.footer.legal.linkUnderlineColorVisited};
        color: ${(p) => p.theme.footer.legal.linkColorVisited};
    }

    ${FooterText}
`;

const LinkGroup = styled.div`
    &:first-child {
        margin-right: auto;
    }
    display: flex;
    flex-wrap: wrap;
    align-items: center;
`;

const TextLinkGroup = styled(LinkGroup)`
    > :not(:last-child) {
        margin-right: 8px;

        ${breakpoint.medium} {
            margin-right: 18px;
        }
    }
`;

const SocialLinkGroup = styled(LinkGroup)`
    color: ${(p) => p.theme.footer.legal.linkColor};
    > :not(:last-child) {
        margin-right: 13px;
    }

    ${breakpoint('small', { down: true })} {
        display: none;
    }
`;

const StyledZee = styled(Zee)`
    display: none;
    ${breakpoint.medium} {
        display: inline-block;
        font-size: 36px;
        margin-right: 17px;
    }
`;

const CopyrightNotice = styled.div`
    display: block;
    width: 100%;
    ${breakpoint.medium} {
        display: flex;
        align-items: center;
        width: auto;
    }
`;

const DesktopOnly = styled.div`
    display: none;
    margin: 0;
    padding: 0;
    border: 0;
    ${breakpoint.medium} {
        display: block;
    }
`;

export default withTheme(Legal);
